import { Navigate, useLocation } from "react-router-dom";
import { isAuthenticated, getCurrentRole } from "../../data/users";
import routes from "../../routes";
import { canRoleAccessPath, getDefaultRouteForRole, hasMatchingRoute } from "../../utils/routeAccess";

const DRIVER_ONLY_PATH = "/driver";
const LOGIN_PATH = "/login";
const LOGOUT_PATH = "/logout";
const ACCESS_NOTICE_KEY = "ci_access_notice";

function storeAccessNotice(pathname, role) {
  try {
    const message =
      role === "dispatcher"
        ? "Dispatcher access is limited to Courier Dashboard, Driver Status, Live Map, Create Task, Tasks, and Route Planner."
        : "You do not have access to that page.";
    window.sessionStorage.setItem(
      ACCESS_NOTICE_KEY,
      JSON.stringify({ message, deniedPath: pathname, role, createdAt: new Date().toISOString() })
    );
  } catch {
    // ignore storage failures
  }
}

/**
 * Protects routes that require authentication.
 * Use for the main app shell — wrap all protected content.
 */
export function AuthGuard({ children }) {
  const { pathname } = useLocation();
  const isLoggedIn = isAuthenticated();
  const role = getCurrentRole();
  const defaultRoute = getDefaultRouteForRole(role);

  if (!isLoggedIn) {
    if (pathname === LOGIN_PATH) return children;
    return <Navigate to={LOGIN_PATH} replace />;
  }

  if (pathname === LOGIN_PATH) {
    return <Navigate to={defaultRoute} replace />;
  }

  if (pathname === LOGOUT_PATH) {
    return children;
  }

  const matchedRoute = hasMatchingRoute(routes, pathname);
  if (matchedRoute && !canRoleAccessPath(routes, role, pathname)) {
    storeAccessNotice(pathname, role);
    return <Navigate to={defaultRoute} replace />;
  }

  return children;
}

/**
 * Wraps a route element. Use for individual routes if needed.
 * Optional per-route guard if a page needs an extra explicit role gate.
 */
export function ProtectedRoute({ children, requireAdmin = false, allowedRoles = null }) {
  const { pathname } = useLocation();
  const isLoggedIn = isAuthenticated();
  const role = getCurrentRole();
  const defaultRoute = getDefaultRouteForRole(role);
  const effectiveRoles = requireAdmin ? ["admin"] : allowedRoles;

  if (!isLoggedIn) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  if (effectiveRoles && !effectiveRoles.includes(role)) {
    storeAccessNotice(pathname, role);
    return <Navigate to={defaultRoute} replace />;
  }

  if (role === "driver" && pathname !== DRIVER_ONLY_PATH && pathname !== LOGOUT_PATH) {
    return <Navigate to={DRIVER_ONLY_PATH} replace />;
  }

  return children;
}
